import { withStyles } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { History } from "history";
import { observer, Observer } from "mobx-react";
import React from "react";
import { Router } from "react-router";
import "reflect-metadata";

import { store } from "@store";
import { openInNewWindow } from "@utils/openInNewWindow";

import { getSignInAsUserEmail } from "@api/fetch";
import locale from "@bonliva-crm/locale";
import { ConfirmDialog } from "@containers";
import { ImageCropperDialog } from "@containers/ImageCropperDialog/ImageCropperDialog";
import { OutdatedOverlay } from "@containers/OutdatedOverlay";
import { WorkflowTasksOverlay } from "@containers/WorkflowTasksOverlay";
import { NavigationProvider, Navigator } from "@navigation";
import { themeGlobals } from "@theme";
import { ErrorDialog, ModalSlider, PrimaryButton, Spinner } from "./common";
import { AppDrawer } from "./common/AppDrawer";
import SiteLayout from "./common/layouts/SiteLayout";
import DnDInput from "./containers/DnDContainer/DnDInput";
import DnDRoot from "./containers/DnDContainer/DnDRoot";
import { AppTitleHelmet } from "./common/app-title-helmet";
import EmailEditorDialog from "./containers/EmailEditor/EmailEditorDialog";
import MainMenu from "./containers/MainMenu/MainMenu";
import Upload from "./containers/Upload/Upload";

import UserSelector from "./containers/UserSelector/UserSelector";
import { WithGlobalUserSelector } from "./containers/user-selector-modal/with-global-user-selector";
import Routes from "./routes";
import { TenantProvider } from "./tenant";

// Initialize numeral locale
import "@bonliva-crm/shared/numeral-config";
import { DbNameContextProvider } from "./context/db-name-context";
import { SidebarLockProvider } from "./context/sidebar-lock-context";

const RouterFixedTypes = Router as any;

type Props = {
  history: History;
};

type State = {
  error: any | null;
  errorInfo: string | null;
};

/**
 * App Container
 */
@observer
class App extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      errorInfo: null
    };
  }

  componentDidCatch(error, info) {
    this.setState({ error, errorInfo: info.componentStack });
  }

  renderError = () => {
    const { error: errorState, errorInfo } = this.state;

    const error = store().error || errorState;
    const hasError = Boolean(error);
    let errorDetails: string;
    let errorHeader: string;
    let errorText: string;

    if (hasError) {
      errorHeader = store().error
        ? store().error.name || "Unexpected error"
        : "Unexpected error";

      errorText = store().error ? store().error.toString() : error.toString();

      errorDetails = error.stack || errorInfo;
    }

    return (
      <ErrorDialog
        open={hasError}
        header={errorHeader}
        message={errorText}
        errorDetails={errorDetails}
        controls={
          <PrimaryButton
            leftIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            {locale.ReloadPage}
          </PrimaryButton>
        }
      />
    );
  };

  renderLoading = () => {
    return (
      <div style={{ height: "98vh", display: "flex" }}>
        <Spinner />
      </div>
    );
  };

  renderApp = () => {
    const { ui, user, upload, emailEditor } = store();
    const { myProfile } = user;

    if (!myProfile) {
      return null;
    }

    const signInAs = getSignInAsUserEmail();

    const routerContent = myProfile.Permissions && (
      <SiteLayout
        forceSidebarStay={false}
        sideBar={
          <AppDrawer
            mainMenu={<MainMenu />}
            onLoginAsClick={() => {
              ui.userSelector.open({
                onSelect: (user) => {
                  openInNewWindow({ singInAsUser: user.Email });
                }
              });
            }}
            onLogout={() => this.props.history.push("/logout")}
            showLoginAs={myProfile.Permissions.CanPose}
          />
        }
        content={<Routes />}
      />
    );

    return (
      <WithGlobalUserSelector>
        <TenantProvider>
          <DbNameContextProvider>
            <SidebarLockProvider>
            <div style={{ display: "flex", height: "100%", width: "100%" }}>
              <AppTitleHelmet signInAs={signInAs} />

              <RouterFixedTypes history={this.props.history}>
                <DnDRoot>{routerContent}</DnDRoot>
              </RouterFixedTypes>

              {store()
                .navigation.getInstances()
                .map((navigation, i) => (
                  <DnDRoot key={navigation.name}>
                    <NavigationProvider navigation={navigation} key={navigation.name}>
                      <Observer>
                        {() => (
                          <ModalSlider
                            open={!!navigation.location}
                            onClose={() =>
                              store().navigation.clearInstance(navigation.name)
                            }
                            offsetLevel={i}
                            navigationName={navigation.name}
                          >
                            <Navigator />
                          </ModalSlider>
                        )}
                      </Observer>
                    </NavigationProvider>
                  </DnDRoot>
                ))}

              <UserSelector store={ui.userSelector} />

              <Upload />

              <DnDInput />

              <ImageCropperDialog
                open={upload.showImageCropperDialog}
                source={upload.imageCropperDialogFileUrl}
              />

              <EmailEditorDialog store={emailEditor} />

              <ConfirmDialog store={ui.confirmDialog} />

              <OutdatedOverlay />
              <WorkflowTasksOverlay />
            </div>
            </SidebarLockProvider>
          </DbNameContextProvider>
        </TenantProvider>
      </WithGlobalUserSelector>
    );
  };

  render() {
    const { error } = this.state;

    if (store().error || error) {
      return this.renderError();
    }

    if (store().isLoading) {
      return this.renderLoading();
    }

    return this.renderApp();
  }
}

export default withStyles(themeGlobals)(App);
